import DeleteOutlined from "@mui/icons-material/DeleteOutlined";
import DriveFileMoveOutlined from "@mui/icons-material/DriveFileMoveOutlined";
import MoreVert from "@mui/icons-material/MoreVert";
import RestoreFromTrash from "@mui/icons-material/RestoreFromTrash";
import {
  Box,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import { useState } from "react";
import { useDialog } from "../../../utils/hooks/use-dialog";
import { ArchiveDialog } from "../../generic/components/archive-dialog";
import { CopyAction } from "../../moment-card/components/actions/copy";
import { useArchiveMoment } from "../../moment-card/hooks/use-archive-moment";
import { MoveDialog } from "./move-dialog";

import type { ProgramNode, ProgramTree } from "../../program-tree/program-tree";
import type { TimelineRow } from "../program-timeline";
import type { FC } from "react";

interface ActionsProps {
  row: TimelineRow;
  tree: ProgramTree;
  /** After it is moved, moved back, archived or unarchived. */
  onChanged: () => void;
}

const Actions: FC<ActionsProps & { programId: ProgramNode["id"] }> = (props) => {
  const { row, programId, tree, onChanged } = props;
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const close = (): void => setAnchor(null);
  const moveDialog = useDialog();
  const archiveDialog = useDialog();
  // Only a finished Communication is paused before it is archived.
  const { archive, archiving } = useArchiveMoment(
    row.id,
    row.status === "finished" ? "finished" : null,
  );
  const cannotArchive = row.status === "active";
  const archived = row.status === "archived";

  const archiveItem = (
    <MenuItem
      disabled={cannotArchive || archiving}
      onClick={() => {
        close();
        archiveDialog.handleOpen();
      }}
      sx={archived ? undefined : { color: "error.main" }}
      data-analytics-id={archived ? "program-timeline-unarchive" : "program-timeline-archive"}
    >
      <ListItemIcon>
        {archived ? (
          <RestoreFromTrash fontSize="small" />
        ) : (
          <DeleteOutlined fontSize="small" color="error" />
        )}
      </ListItemIcon>
      <ListItemText>{archived ? "Unarchive" : "Archive"}</ListItemText>
    </MenuItem>
  );

  return (
    // Above the title's link, which covers the card or row.
    <Box sx={{ position: "relative", zIndex: 1, display: "flex", flexShrink: 0 }}>
      <CopyAction id={row.id} title={row.title} clonable />
      <Tooltip title={`More actions: ${row.title}`}>
        <IconButton
          onClick={(event) => setAnchor(event.currentTarget)}
          aria-haspopup="menu"
          aria-expanded={Boolean(anchor)}
          data-analytics-id="program-timeline-menu"
        >
          <MoreVert fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          onClick={() => {
            close();
            moveDialog.handleOpen();
          }}
          data-analytics-id="program-timeline-move"
        >
          <ListItemIcon>
            <DriveFileMoveOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>Move to…</ListItemText>
        </MenuItem>
        {cannotArchive ? (
          // A disabled item takes no pointer events, so the span shows its tooltip.
          <Tooltip title="Archive is unavailable for active Communications. Please pause the Communication first.">
            <span>{archiveItem}</span>
          </Tooltip>
        ) : (
          archiveItem
        )}
      </Menu>
      <MoveDialog
        open={moveDialog.open}
        onClose={moveDialog.handleClose}
        row={row}
        programId={programId}
        tree={tree}
        onMoved={onChanged}
      />
      <ArchiveDialog
        open={archiveDialog.open}
        onClose={archiveDialog.handleClose}
        kind="Communication"
        unarchive={archived}
        archiving={archiving}
        analyticsId="program-timeline"
        // Stays open, loading, until it's done.
        onConfirm={() => {
          void (async () => {
            try {
              if (await archive(!archived)) onChanged();
            } finally {
              archiveDialog.handleClose();
            }
          })().catch(console.error);
        }}
      />
    </Box>
  );
};

/**
 * Copy, and a menu to move and to archive or unarchive, for a Communication in a program or folder
 * that the caller can edit. An active one must be paused before it is archived.
 */
export const CommunicationActions: FC<ActionsProps> = ({ tree, ...props }) => {
  const { program_id } = props.row;
  return program_id && tree.byId.get(program_id)?.editable ? (
    <Actions {...props} tree={tree} programId={program_id} />
  ) : null;
};
